<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  applyPlatformDocumentLocale,
  normalizePlatformLocale,
} from 'dashboard/helper/platformLocale';

import FormSelect from 'v3/components/Form/Select.vue';

defineProps({
  label: { type: String, default: '' },
  description: { type: String, default: '' },
});

const { t, locale } = useI18n();
const { updateUISettings, uiSettings } = useUISettings();
const { enabledLanguages } = useConfig();
const { currentAccount } = useAccount();
const isSaving = ref(false);

const currentLanguage = computed(() => {
  const selectedLocale = uiSettings.value?.locale;
  return selectedLocale ? normalizePlatformLocale(selectedLocale) : '';
});

const languageOptions = computed(() => [
  {
    name: t(
      'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.USE_ACCOUNT_DEFAULT'
    ),
    iso_639_1_code: '',
  },
  ...(enabledLanguages ?? []),
]);

const updateLanguage = async languageCode => {
  if (isSaving.value) return;
  isSaving.value = true;
  try {
    if (!languageCode) {
      // Clear preference to use account default
      if (!(await updateUISettings({ locale: null })))
        throw new Error('Preference was not saved');
      const accountLocale = normalizePlatformLocale(
        currentAccount.value.locale
      );
      applyPlatformDocumentLocale(accountLocale);
      locale.value = accountLocale;
      useAlert(
        t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_SUCCESS')
      );
      return;
    }

    const valid = (enabledLanguages || []).some(
      l => l.iso_639_1_code === languageCode
    );
    if (!valid) {
      throw new Error(`Invalid language code: ${languageCode}`);
    }

    if (!(await updateUISettings({ locale: languageCode })))
      throw new Error('Preference was not saved');
    // Apply immediately if the user explicitly chose a preference
    const selectedLocale = normalizePlatformLocale(languageCode);
    applyPlatformDocumentLocale(selectedLocale);
    locale.value = selectedLocale;

    useAlert(
      t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_SUCCESS')
    );
  } catch (error) {
    useAlert(
      t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const selectedValue = computed({
  get: () => currentLanguage.value,
  set: value => {
    updateLanguage(value);
  },
});
</script>

<template>
  <div class="flex gap-2 justify-between w-full items-start">
    <div>
      <label
        for="profile-language"
        class="text-n-gray-12 font-medium leading-6 text-sm"
      >
        {{ label }}
      </label>
      <p class="text-n-gray-11">
        {{ description }}
      </p>
    </div>
    <FormSelect
      id="profile-language"
      v-model="selectedValue"
      name="language"
      :disabled="isSaving"
      spacing="compact"
      class="min-w-28 mt-px"
      :options="languageOptions"
      label=""
    >
      <option
        v-for="option in languageOptions"
        :key="option.iso_639_1_code || 'default'"
        :value="option.iso_639_1_code"
        :selected="option.iso_639_1_code === selectedValue"
      >
        {{ option.name }}
      </option>
    </FormSelect>
  </div>
</template>
